import React, { useEffect, useRef } from 'react';

const WaveBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let frame = 0;

    // easyTEK brand aurora: locked to the blue/indigo band (h 200-224), no purple/pink.
    // Declared BEFORE paintStatic/resize because resize() runs synchronously on mount
    // and, under reduced-motion, calls paintStatic() which reads these (avoid TDZ crash).
    const ribbons = [
      { h: 202, s: 80, speed: 0.003300, offset: 0.00, widthRatio: 0.105, alpha: 0.55 },
      { h: 208, s: 72, speed: 0.003500, offset: 0.70, widthRatio: 0.095, alpha: 0.48 },
      { h: 218, s: 60, speed: 0.002900, offset: 1.40, widthRatio: 0.090, alpha: 0.42 },
      { h: 224, s: 55, speed: 0.003100, offset: 2.10, widthRatio: 0.085, alpha: 0.38 },
      { h: 200, s: 85, speed: 0.003000, offset: 2.80, widthRatio: 0.080, alpha: 0.45 },
    ];

    const drawRibbon = (t, r) => {
      const W = canvas.width;
      const H = canvas.height;
      const angle = t * r.speed + r.offset;

      const ox = W * 0.02;
      const oy = H * 0.50 + Math.sin(angle * 0.9) * H * 0.12;

      const sweep = angle * 0.7;
      const cx1 = W * 0.30 + Math.sin(sweep) * W * 0.08;
      const cy1 = oy + Math.cos(sweep) * H * 0.32;
      const cx2 = W * 0.65 + Math.sin(sweep + 0.55) * W * 0.08;
      const cy2 = oy + Math.cos(sweep + 0.55) * H * 0.36;
      const ex  = W * 0.98;
      const ey  = H * 0.50 + Math.cos(sweep + 1.1) * H * 0.28;

      const grad = ctx.createLinearGradient(ox, oy, ex, ey);
      grad.addColorStop(0,   `hsla(${r.h}, ${r.s}%, 52%, 0)`);
      grad.addColorStop(0.25,`hsla(${r.h}, ${r.s}%, 56%, ${r.alpha})`);
      grad.addColorStop(0.65,`hsla(${r.h + 10}, ${r.s}%, 60%, ${r.alpha * 0.85})`);
      grad.addColorStop(1,   `hsla(${r.h + 18}, ${r.s}%, 64%, 0)`);

      ctx.save();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.bezierCurveTo(cx1, cy1, cx2, cy2, ex, ey);
      ctx.strokeStyle = grad;
      ctx.lineWidth = W * r.widthRatio;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();
    };

    const paintStatic = () => {
      ctx.fillStyle = '#080d1e';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ribbons.forEach((r) => drawRibbon(0, r));
    };
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Resetting width/height clears the canvas; under reduced-motion we
      // re-paint the static frame so the background doesn't go blank.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        paintStatic();
      }
    };
    resize();
    window.addEventListener('resize', resize);

    const FPS = 20;
    const FRAME_INTERVAL = 1000 / FPS;
    let lastTime = 0;

    const draw = (now) => {
      animId = requestAnimationFrame(draw);

      const delta = now - lastTime;
      if (delta < FRAME_INTERVAL) return;
      lastTime = now - (delta % FRAME_INTERVAL);

      const W = canvas.width;
      const H = canvas.height;

      if (frame === 0) {
        ctx.fillStyle = '#080d1e';
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.fillStyle = 'rgba(8, 13, 30, 0.18)';
        ctx.fillRect(0, 0, W, H);
      }

      ribbons.forEach((r) => drawRibbon(frame, r));

      frame++;
    };

    // prefers-reduced-motion: paint one static composed frame, no rAF loop.
    // (resize handler re-paints on window resize since width/height changes clear the canvas.)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      paintStatic();
    } else {
      animId = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        display: 'block',
      }}
    />
  );
};

export default WaveBackground;
